import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Search, Phone, Mail } from "lucide-react";
import { catOf } from "@/lib/crm";

/** Ricerca rapida (nome / numero / e-mail) su tutti gli archivi per associare manualmente una conversazione. */
export function ContactPicker({ open, onOpenChange, onPick, initialQuery = "", title = "Associa a un contatto esistente" }) {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setQ(initialQuery || ""); }, [open, initialQuery]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return undefined; }
    const h = setTimeout(() => {
      setLoading(true);
      api.get(`/contacts/search?q=${encodeURIComponent(q.trim())}`).then(({ data }) => setResults(data.results || [])).catch(() => setResults([])).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(h);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border bg-brema-card" data-testid="contact-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">{title}</DialogTitle>
          <DialogDescription>Cerca per nome, numero o e-mail tra Rivenditori, Ambassador, Testate e contatti WhatsApp. Nessun dato viene sovrascritto: il numero viene aggiunto all'anagrafica solo se mancante.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Es. Ciclosport, +39 348…, nome@dominio.it" className="pl-9" data-testid="contact-picker-input" />
        </div>
        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto" data-testid="contact-picker-results">
          {loading && <div className="py-4 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}
          {!loading && q.trim().length >= 2 && results.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground" data-testid="contact-picker-empty">Nessun contatto trovato.</p>}
          {results.map((r) => (
            <button key={`${r.type}-${r.id}`} type="button" onClick={() => onPick(r)} data-testid={`contact-picker-item-${r.type}-${r.id}`}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-brema-bg p-2.5 text-left transition-colors hover:border-primary/40 hover:bg-brema-hover">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-sm font-semibold text-foreground">{r.name}</span>
                  <span className="rounded-full bg-brema-card px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{r.type_label}</span>
                  {(r.categories || []).filter((c) => c !== (r.type === "wa_contact" ? "" : "")).slice(0, 3).map((c) => <span key={c} className={`rounded-full px-1.5 py-0.5 text-[10px] ${catOf(c).cls}`}>{catOf(c).label}</span>)}
                  {r.strength === "strong" && <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">numero corrispondente</span>}
                  {r.strength === "weak" && <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">numero simile</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                  {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                  {r.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>}
                </div>
              </div>
              <span className="shrink-0 text-xs font-semibold text-brand">Collega →</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
